import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { KeycloakService } from 'keycloak-angular';
import { lastValueFrom } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { DocumentViewerComponent } from 'src/app/shared/components/document-viewer/document-viewer.component';
import { ModalDialogComponent } from 'src/app/shared/components/modal-dialog/modal-dialog.component';
import { ElementoFiltro } from 'src/app/shared/models/elementoFiltro.model';
import { CompromisoService } from 'src/app/shared/services/compromiso.service';
import { ElementService } from 'src/app/shared/services/element.service';
import { SuscripcionService } from 'src/app/shared/services/suscripcion.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-commitment-detail',
  templateUrl: './commitment-detail.component.html',
  styleUrls: ['./commitment-detail.component.scss'],
})
export class CommitmentDetailComponent implements OnInit {
  returnUrl: string | undefined;
  constructor(
    private compromisoService: CompromisoService,
    private router: Router,
    private route: ActivatedRoute,
    private elementService: ElementService,
    private suscriptionService: SuscripcionService,
    private modalService: NgbModal,
    private keycloakService: KeycloakService,
  ) {}
  idCompromiso!: string;
  compromiso!: any;
  members: any[] = [];
  apps: any[] = [];
  token: string = '';
  tieneSuscripcionesPendientes: boolean = false;
  returnLabel = '';
  compromisoUrl = `${environment.url_sincronia_compromisos}`;
  buttonText = 'COMPROMISO EN BLOCKCHAIN';
  ngOnInit(): void {
    this.keycloakService.getToken().then(token => {
      this.token = token;
      if (this.token) this.buttonText = 'COMPROMISO EN BLOCKCHAIN';
      else this.buttonText = 'UNITE a la PLATAFORMA';
    });

    this.route.params
      .pipe(
        map(params => params['id']),
        tap(id => {
          this.idCompromiso = id;
          this.compromisoUrl += this.idCompromiso;
        }),
        switchMap(id => this.compromisoService.getCompromiso(id))
      )
      .subscribe(async (data: any) => {
        this.compromiso = data['commitments'][0];
        const filter = new ElementoFiltro();
        filter.modulo = 'Comunidades';
        filter.fuente = this.compromiso.community;

        const community = await lastValueFrom(
          this.elementService.getAll(0, 0, filter, ''),
        );

        // Handle case where community data is not available
        if (!community || !community.elementos || community.elementos.length === 0) {
          console.warn('Community data not found for:', this.compromiso.community);
          this.returnUrl = '/communities';
          this.returnLabel = 'Comunidades';
        } else {
          this.returnUrl = history.state.comingFrom
            ? `comunidades/${community.elementos[0].id}`
            : `compromisos-comunidades/${community.elementos[0].id}`;

          this.returnLabel = history.state.comingFrom
            ? `Comunidad ${this.compromiso.community}`
            : `Compromisos de Comunidad ${this.compromiso.community}`;
        }

        const resp = await this.compromisoService.getMembersPorCompromiso(
          this.compromiso.community,
          this.idCompromiso,
        );

        this.members = resp.adherence
          .filter((adherence: any) => adherence.applicationId === 0)
          .map((adherence: any) => ({
            ...adherence,
            statusText: this.getUserStatusInCommitment(adherence.status),
            deadlineDate: this.getFechaLimiteInCommitment(
              adherence.deadlineDate,
            ),
            type: adherence.type === 1 ? 'Persona' : 'Empresa',
            documents: adherence.documents || [],
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));

        this.apps = resp.adherence
          .filter((app: any) => app.applicationId !== 0)
          .map((app: any) => ({
            ...app,
            name: app.applicationName,
            statusText: this.getUserStatusInCommitment(app.status),
            deadlineDate: this.getFechaLimiteInCommitment(app.deadlineDate),
            companyName: app.name,
            documents: app.documents || [],
          }));
      });

    this.suscriptionService
      .hasPendingSuscription()
      .subscribe(hasPendingSuscription => {
        this.tieneSuscripcionesPendientes = hasPendingSuscription;
      });
  }

  getUserStatusInCommitment(status: number): string {
    switch (status) {
      case 1:
        return '💬 Tiene intención';
      case 2:
        return '✅ Cumple';
      default:
        return 'No cumple';
    }
  }

  getFechaLimiteInCommitment(deadlineDate: string): string {
    if (!deadlineDate || deadlineDate === '1970-01-01T00:00:00.000Z') return '';
    const date = new Date(deadlineDate);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  formatDocumentDate(createdAt: string): string {
    if (!createdAt) return '';
    const date = new Date(createdAt);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}/${month}/${day}`;
  }

  openPlataforma() {
    if (this.suscriptionService.isSuscriptorSync()) {
      if (this.suscriptionService.memberId != null) {
        window.open(
          `${this.compromisoUrl}?user_id=${this.suscriptionService.memberId}&token=${this.token}`,
          '_blank',
        );
      } else {
        window.open(
          `${this.compromisoUrl}?user_id=${this.suscriptionService.idClienteConfirmado}&token=${this.token}`,
          '_blank',
        );
      }
    } else {
      this.router.navigate(['/unite']);
    }
  }

  openModal(texto: string) {
    const modalRef = this.modalService.open(ModalDialogComponent, {
      centered: true,
      size: 'lg',
      modalDialogClass: 'person--dialog',
    });
    modalRef.componentInstance.texto = texto;
  }

  openDocumentViewer(documentId: string): void {
    this.compromisoService.getDocumentDetails(documentId).subscribe({
      next: documentInfo => {
        const modalRef = this.modalService.open(DocumentViewerComponent, {
          centered: true,
          size: 'xl',
          modalDialogClass: 'document-viewer-modal',
        });
        modalRef.componentInstance.document = documentInfo;
      },
      error: err => {
        console.error('Error al obtener detalles del documento:', err);
        this.openModal(
          'No se pudo cargar el documento. Por favor, intente nuevamente.',
        );
      },
    });
  }
}
